import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FinancialRatios } from "./FinancialRatios";
import ActionButtons from "./financial/ActionButtons";
import BalanceSheetTab from "./financial/BalanceSheetTab";
import ProfitLossTab from "./financial/ProfitLossTab";
import CashFlowTab from "./financial/CashFlowTab";
import UploadFinancialsModal from "./financial/UploadFinancialsModal";
import { toast } from "../hooks/use-toast";
import { FinancialDataType } from "../lib/csvUtils";
import { FinancialData } from "../types/FinancialInterface";
import { getDefaultRecords } from "../utils/financialTop3consolidate";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Button } from "./ui/button";
import { AlertTriangle, ArrowRight, Brain, ChevronDown, ChevronUp, Eye, EyeOff, Lightbulb, Sparkles, Star, Target, ThumbsDown, ThumbsUp, TrendingUp } from "lucide-react";
import apiService from "../services/apiService";
import { calculateFinancialRatios, calculateLLPFinancialRatios } from "../utils/financialRatios";
// import { GPTResponse } from '../../services/gptService';
interface FinancialTabProps {
  financials: FinancialData[];

  financialupload: (payload: FinancialData[], type: string, nature: string) => void;
  customerId:String;
  ratioActionButton:string;
  financialrefresh: () => Promise<void>;
}

interface ApiResponse<T> {
  data: T;
  likeBtn?:number
  actionButton?:string;
  company?: any;
  gstEpfo?: {
    gst_details: Array<any>;
    establishments_registered_with_epfo: Array<any>;
  };
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

const FinancialTab: React.FC<FinancialTabProps> = ({ financials, financialupload,customerId, ratioActionButton,financialrefresh}) => {

  // States
  const [activeTab, setActiveTab] = useState<"bs" | "pnl" | "cf">("bs");
  const [isOnePagerOpen, setIsOnePagerOpen] = useState(false);
  const [isRatiosDrawerOpen, setIsRatiosDrawerOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // State for financial data array
  const [currentFinancialData, setCurrentFinancialData] = useState<FinancialData[]>([]);
  const [feedback, setFeedback] = useState<1 | 0 | null>(null);
  const [data,SetData]=useState<any>({})
  const [loading, setLoading] = useState(false);

  const handleFeedback = async (type: 1 | 0) => {
    try {
      const response: any = await apiService.post("customers/financials/update-gpt-like", {
        customerId,
        type,
        selectedSegment:null
      });
        setFeedback(type);
    } catch (e: any) {
      
    }
};

  // Correctly sync props into state only via useEffect
  useEffect(() => {
    const data = Array.isArray(financials) ? financials : [];
    setCurrentFinancialData(data);
    // Debug: Log financial data structure
   
  }, [financials]);

  useEffect(()=>{
    getChatGptResponse();
  },[])

  const getChatGptResponse = async () =>{
    const [response] = await Promise.all([
      apiService.get<ApiResponse<any>>(`/customers/financials/get-response/${customerId}`)
    ]);
    SetData(response.data)
    setFeedback(
      response.likeBtn === 0 || response.likeBtn === 1
          ? response.likeBtn
          : null
  );
      financialrefresh()
    
  }

  const top3Consolidated = getDefaultRecords(currentFinancialData);


  // Detect if this is LLPIN data (check if any record was transformed from LLPIN)
  const isLLPIN = top3Consolidated.some((f: any) => 
    (f as any).isLLPIN || 
    (f as any).statement_of_assets_and_liabilities || 
    (f as any).statement_of_income_and_expenditure
  );

  // If Cash Flow tab is active but data is LLPIN, switch to Balance Sheet
  useEffect(() => {
    if (isLLPIN && activeTab === "cf") {
      setActiveTab("bs");
    }
  }, [isLLPIN, activeTab]);


  const handleDataSave = (type: FinancialDataType, data: FinancialData[]) => {
    financialupload(data, type, top3Consolidated[0]?.nature || "Standalone")
    // setCurrentFinancialData(data);
    toast({
      title: "Financial Data Updated",
      description: `${type.replace(/-/g, " ").replace(/^\w/, m => m.toUpperCase())} data has been updated.`,
    });
  };


    // Parse the GPT response if it's a text response, otherwise use structured data

    let displayRisks = data?.key_credit_risks || [];
    let displayStrengths = data?.key_strengths || [];
    let displayRecommendations = data?.underwriting_insights_and_decision_signal || [];

   const handleRatiosDrawerOpen = ()=>{
    
    if(ratioActionButton == 'Regenerate AFIN Overview' || ratioActionButton == 'AFIN Overview' ){
      updateFinancialData()
    }else{
      setIsRatiosDrawerOpen(true)
    }
    }

    const updateFinancialData = async () => {
      let data
      if(isLLPIN){
        data = calculateLLPFinancialRatios(top3Consolidated);
      }else{  
        data = calculateFinancialRatios(top3Consolidated);
      }
      
      try {
        setLoading(true);
        const response: any = await apiService.post("customers/financials/update-ratio", {
          customerId,
          data
        });   
          getChatGptResponse()
      } catch (e: any) {
        setLoading(false);
      }finally{
        setLoading(false);
      }
    };
   

  return (
    <div className="space-y-6">
      <ActionButtons
        loading={loading}
        onOpenRatiosDrawer={handleRatiosDrawerOpen}
        onOpenOnePager={() => setIsOnePagerOpen(true)}
        onOpenUploadModal={() => setIsUploadModalOpen(true)}
        financialData={top3Consolidated}
        customerId={customerId.toString() || ''}
        ratioActionButton = {ratioActionButton}
      />


    {data?.financial_position && (<div className="relative">
      {/* Modern AI-styled container */}
      <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#9E7926]/3 via-transparent to-[#489BA8]/3 pointer-events-none" />
        
        {/* Header Section */}
        <div className="relative p-6 border-b border-gray-100/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9E7926] to-[#489BA8] p-0.5">
                  <div className="w-full h-full rounded-xl bg-white/95 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-[#9E7926]" />
                  </div>
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-[#489BA8] to-[#9E7926] rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                AFIN Overview
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  AI-Powered Underwriting Intelligence
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="relative p-6 space-y-8">
          {/* Executive Summary */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9E7926]/20 to-[#489BA8]/20 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-[#9E7926]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Executive Summary</h3>
              </div>
            </div>
            <div className="relative">
              <div className="relative p-6 rounded-xl bg-gradient-to-br from-[#9E7926]/5 to-[#489BA8]/5 border border-[#9E7926]/10">
                <p className="text-gray-700 leading-relaxed">
                  {data?.financial_position}
                </p>
              </div>
            </div>
          </section>

          {/* Collapsible Detailed Analysis */}
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-center gap-3 h-11 rounded-xl border-2 border-gray-200 hover:border-[#9E7926]/30 hover:bg-gray-50/50 transition-all duration-200"
              >
                {isOpen ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="font-medium">{isOpen ? 'Hide' : 'Show'} Detailed Analysis</span>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="space-y-8 mt-6">
              {/* Key Risks */}
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Key Credit Risks</h4>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {displayRisks.map((risk: any, index: number) => (
                    <div key={index} className="flex items-start space-x-3 group">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-sm font-medium mt-0.5">
                        {index + 1}
                      </div>
                      <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{risk}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Key Strengths */}
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Key Strengths</h4>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {displayStrengths.map((strength: any, index: number) => (
                    <div key={index} className="flex items-start space-x-3 group">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-50 flex items-center justify-center mt-0.5">
                        <Star className="w-4 h-4 text-green-500" />
                      </div>
                      <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{strength}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Recommendations */}
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Target className="w-4 h-4 text-blue-500" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">Underwriting insights and decision signal</h4>
                  </div>
                  
                 
                </div>
                
                <div className="space-y-3">
                  {displayRecommendations.map((rec: any, index: number) => (
                    <div key={index} className="flex items-start space-x-3 group">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center mt-0.5">
                        <ArrowRight className="w-4 h-4 text-blue-500" />
                      </div>
                      <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{rec}</p>
                    </div>
                  ))}
                </div>
                
               
              </section>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Feedback Section */}
        <div className="relative px-6 py-4 border-t border-gray-100/50 bg-gray-50/30">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-600 font-medium">Was this analysis helpful?</p>

                    <div className="flex space-x-2">
                        <Button
                            variant={feedback === 1 ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleFeedback(1)}
                            className={`h-8 px-3 rounded-full text-xs ${feedback === 1
                                ? 'bg-green-500 hover:bg-green-600 text-white'
                                : 'border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            <ThumbsUp className="w-3 h-3 mr-1" />
                            Yes
                        </Button>

                        <Button
                            variant={feedback === 0 ? 'destructive' : 'outline'}
                            size="sm"
                            onClick={() => handleFeedback(0)}
                            className={`h-8 px-3 rounded-full text-xs ${feedback === 0
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            <ThumbsDown className="w-3 h-3 mr-1" />
                            No
                        </Button>
                    </div>
                </div>

                {feedback !== null && feedback !== undefined && (
                    <p className="text-sm text-green-600 font-medium">
                        Thank you for your feedback! ✨
                    </p>
                )}
            </div>
        </div>
      </div>
    </div>)}


  


      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "bs" | "pnl" | "cf")} className="w-full">
        <TabsList className={`mb-4 grid w-full ${isLLPIN ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <TabsTrigger value="bs" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "bs"
            ? "bg-[#f0eadc]"
            : "bg-white"
            }`} >📑 Balance Sheet</TabsTrigger>
          <TabsTrigger value="pnl" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "pnl"
            ? "bg-[#f0eadc]"
            : "bg-white"
            }`} >📊 Profit and Loss</TabsTrigger>
          {!isLLPIN && (
            <TabsTrigger value="cf" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "cf"
              ? "bg-[#f0eadc]"
              : "bg-white"
              }`} >💵 Cash Flow</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="bs">
          <BalanceSheetTab financialData={top3Consolidated} />
        </TabsContent>
        <TabsContent value="pnl" >
          <ProfitLossTab financialData={top3Consolidated} />
        </TabsContent>
        {!isLLPIN && (
          <TabsContent value="cf" >
            <CashFlowTab financialData={top3Consolidated} />
          </TabsContent>
        )}
      </Tabs>

      {isRatiosDrawerOpen && (
        <>
          <div className="fixed inset-y-0 right-0 w-1/2 bg-white border-l shadow-lg z-60 transition-transform duration-300 overflow-y-auto h-full">
            <FinancialRatios className={""} onClose={() => setIsRatiosDrawerOpen(false)} financialData={top3Consolidated} isLLPIN={isLLPIN}  />
          </div>
          <div className="fixed inset-0 bg-black/20 z-55" onClick={() => setIsRatiosDrawerOpen(false)} />
        </>
      )}
      {/* 
      <OnePagerModal
        isOpen={isOnePagerOpen}
        onClose={() => setIsOnePagerOpen(false)}
        financialData={top3Consolidated}
      /> */}
      <UploadFinancialsModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onDataSave={handleDataSave}
        entityType={isLLPIN ? 'LLP' : 'CIN'}
      />
    </div>
  );
};

export default FinancialTab;
